import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Fieldset } from 'primereact/fieldset';
import React, { forwardRef, useCallback, useContext, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ButtonSeplag from '../../../../../../../components/buttonSeplag/ButtonSeplag';
import { ApplicationContext } from '../../../../../../../config/Router';
import PageMessages from '../../../../../../../config/translations/PageMessages';
import Shareds from '../../../../../../../config/translations/Shareds';
import { MultipageContext } from '../../../../../../../core/components/Multipage';
import SimpleTable from '../../../../../../../core/components/simpleTables/SimpleTable';
import { arrayToDateString } from '../../../../../../../core/utils/DateMixins';
import { assignRefs } from '../../../../../../../core/utils/Global';
import outraApuracaoService from '../../../../../../../service/OutraApuracaoService';
import styles from './TableApuracao.module.scss';

function TableApuracao(
    {
        legend = 'Apuração do Resultado',
        showDelete = false,
        showOutrasApuracoes = false,
        className = '',
        apuracao,
        onSave,
        disableActions = false,
        metaId,
        ...rest
    },
    ref
) {
    const { id: idIndicador } = useParams();
    const tableRef = useRef(null);
    const { confirmRef, confirmOkRef, modalApuracaoRef } = useContext(MultipageContext);
    const { situacaoIndicador } = useContext(ApplicationContext);

    const ajustaInputs = useCallback(
        ({ dataApuracao, dataReferencia, ...rest }) => {
            if (Object.keys(rest).length === 0) {
                return null;
            }
            return {
                ...rest,
                dataApuracao: Array.isArray(dataApuracao)
                    ? arrayToDateString(dataApuracao)
                    : dataApuracao,
                dataReferencia: Array.isArray(dataReferencia)
                    ? arrayToDateString(dataReferencia)
                    : dataReferencia,
            };
        },
        [apuracao]
    );

    useEffect(() => {
        const response = ajustaInputs(apuracao ?? {});
        tableRef.current.setLista(response ? [response] : []);
    }, [ajustaInputs]);

    const loadMetas = useCallback(async () => {
        const response = await outraApuracaoService.list({
            params: { search: `indicador.id==${idIndicador}` },
        });
        tableRef.current.setLista(response.map((el) => ajustaInputs(el)));
    }, []);

    useEffect(() => {
        if (showOutrasApuracoes) {
            loadMetas();
        }
    }, []);

    return (
        <Fieldset className={`${styles.fieldset} ${className}`} legend={legend} {...rest}>
            {showOutrasApuracoes && (
                <div className="buttonsRight">
                    <Button
                        icon="pi pi-sliders-h"
                        label="Outras Apurações do Indicador"
                        onClick={() => {
                            if (situacaoIndicador?.codgSituacaoIndicador !== 'ATIVO') {
                                confirmOkRef.current?.show(
                                    PageMessages.sei.metasResultados.apurarResultado
                                        .indicadorNaoAtivoApuracaoAvulsa
                                );
                            } else {
                                modalApuracaoRef.current?.show({
                                    outrasApuracoes: true,
                                    loadMetas,
                                });
                            }
                        }}
                    />
                </div>
            )}
            <SimpleTable ref={assignRefs(ref, tableRef)} paginator={false}>
                <Column field="dataReferencia" header="Data de Referência" />
                <Column field="dataApuracao" header="Data de Apuração" />
                <Column field="descValorApurado" header="Valor Apurado" />
                <Column field="contaAcessoCadastro.nome" header="Usuário" />
                {!disableActions && (
                    <Column
                        header="Ações"
                        style={{ width: showDelete ? '150px' : '120px' }}
                        body={(dados) => {
                            return (
                                <div>
                                    <ButtonSeplag
                                        tipo="info"
                                        type="button"
                                        tooltip="Visualizar"
                                        tooltipOptions={{ position: 'top' }}
                                        icon="pi pi-eye"
                                        onClick={() => {
                                            modalApuracaoRef.current?.show({
                                                meta: { id: metaId, apuracaoMeta: dados },
                                                view: true,
                                                outrasApuracoes: showOutrasApuracoes,
                                                loadMetas: showOutrasApuracoes ? loadMetas : onSave,
                                            });
                                        }}
                                        style={{ marginBottom: '.2em' }}
                                    />
                                    <ButtonSeplag
                                        tipo="warning"
                                        type="button"
                                        tooltip="Editar"
                                        tooltipOptions={{ position: 'top' }}
                                        icon="pi pi-pencil"
                                        onClick={() => {
                                            if (
                                                situacaoIndicador?.codgSituacaoIndicador !== 'ATIVO'
                                            ) {
                                                confirmOkRef.current?.show(
                                                    PageMessages.sei.metasResultados.apurarResultado
                                                        .indicadorNaoAtivoApuracaoAvulsa
                                                );
                                            } else {
                                                modalApuracaoRef.current?.show({
                                                    meta: { id: metaId, apuracaoMeta: dados },
                                                    outrasApuracoes: showOutrasApuracoes,
                                                    loadMetas: showOutrasApuracoes
                                                        ? loadMetas
                                                        : onSave,
                                                });
                                            }
                                        }}
                                        style={{ marginLeft: '.5em', marginBottom: '.2em' }}
                                    />
                                    {showDelete && (
                                        <ButtonSeplag
                                            tipo="danger"
                                            type="button"
                                            tooltip="Excluir"
                                            tooltipOptions={{ position: 'top' }}
                                            icon="pi pi-trash"
                                            className="p-button-danger p-mb-1"
                                            onClick={() =>
                                                confirmRef.current?.show(
                                                    Shareds.form.confirmDelete,
                                                    async () => {
                                                        await outraApuracaoService.remover(dados);
                                                        loadMetas();
                                                    }
                                                )
                                            }
                                            style={{ marginLeft: '.5em' }}
                                            theme="Raised"
                                        />
                                    )}
                                </div>
                            );
                        }}
                    />
                )}
            </SimpleTable>
        </Fieldset>
    );
}

export default forwardRef(TableApuracao);
